import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { SingleDatePicker } from 'react-dates';
import styled from 'styled-components';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import moment, { Moment } from 'moment';
import { color, theme } from '@workboard/wobo-ui';
import omit from 'lodash/omit';
import { isElementInside } from '../../../utils/isElementInside';
import { useUniqueId } from '../../hooks';

const Background = styled('button')``;
const Day = styled('div')``;
const DatePickerWrapper = styled('div')`
  display: inline-block !important;
`;

const resolveStart = (start: moment.MomentInput | number) => {
  if (start === undefined || start === null || start === '') {
    return null;
  }
  if (typeof start === 'number') {
    return moment.unix(start).utc().startOf('day');
  }
  return moment.utc(moment(start).format('YYYY-MM-DD'), 'YYYY-MM-DD');
};

type Props = {
  className: string;
  onChange: (date: Date | null) => void;
  onOutsideClick: (e: React.MouseEvent) => void;
  start?: string | number | Date;
  id: string;
  scrollModalIntoView: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customProps: any;
  disabled: boolean;
};

function DatePicker({
  className,
  onChange = () => null,
  onOutsideClick = () => null,
  start: initialStart,
  id: initialId,
  scrollModalIntoView = false,
  customProps,
  disabled,
  ...props
}: Props) {
  const rootRef = useRef<HTMLDivElement>();
  const [date, setDate] = useState(() => resolveStart(initialStart));
  const [focusedInput, setFocusedInput] = useState<string | null | boolean>(
    null
  );

  const id = useUniqueId(initialId);
  useEffect(() => {
    if (focusedInput && scrollModalIntoView) {
      setTimeout(() => {
        const modalElements = document.getElementsByClassName(
          'SingleDatePicker_picker'
        );
        const modalEle = modalElements && modalElements[0];
        modalEle?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start',
        });
      });
    }
  }, [focusedInput, scrollModalIntoView]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    const elements = root?.querySelectorAll('input, button, [tabindex]');
    const onFocus = () => rootRef.current?.classList.add('clear-visible');
    const onBlur = () => {
      setTimeout(() => {
        if (root && !isElementInside(document.activeElement, root)) {
          root?.classList.remove('clear-visible');
        }
      }, 10);
    };
    if (elements) {
      elements.forEach((el) => {
        el.addEventListener('focus', onFocus);
        el.addEventListener('blur', onBlur);
      });
    }
    return () => {
      if (elements) {
        elements.forEach((el) => {
          el.removeEventListener('focus', onFocus);
          el.removeEventListener('blur', onBlur);
        });
      }
    };
  }, []);

  const handleDateChange = useCallback(
    (d: Moment | null) => {
      setDate(d);
      if (!d) {
        onChange(null);
        return;
      }
      onChange(
        moment
          .utc({
            year: d.year(),
            month: d.month(),
            date: d.date(),
          })
          .toDate()
      );
    },
    [onChange]
  );

  const handleOutsideClick = useCallback(
    (e) => {
      setFocusedInput(null);
      onOutsideClick(e);
      e.stopPropagation();
    },
    [onOutsideClick, setFocusedInput]
  );

  const handleUserKeyPress = (e) => {
    if (e.key === 'Escape') {
      setFocusedInput(null);
      onOutsideClick(e);
    } else if (e.key === 'Enter') {
      setFocusedInput(true);
    }
  };

  const handleUserClick = () => {
    if (!disabled) setFocusedInput(true);
  };

  // fix for nullable dates
  useEffect(() => {
    setDate(resolveStart(initialStart));
  }, [initialStart]);

  const today = moment.utc().startOf('day');

  return (
    <DatePickerWrapper
      className={className}
      {...omit(customProps, ['data-testid', 'aria-label'])}
      data-testid={customProps?.['data-testid'] ?? 'wbui-single-date-picker'}
      onKeyUp={handleUserKeyPress}
      onClick={handleUserClick}
      ref={rootRef}
    >
      <SingleDatePicker
        date={date}
        id={id}
        onDateChange={handleDateChange}
        focused={focusedInput}
        onFocusChange={({ focused }) => {
          setFocusedInput(focused);
        }}
        inputIconPosition="before"
        readOnly
        showDefaultInputIcon
        displayFormat={() => 'MMM D, YYYY'}
        numberOfMonths={1}
        hideKeyboardShortcutsPanel
        verticalSpacing={0}
        reopenPickerOnClearDate={false}
        isOutsideRange={() => false}
        disabled={disabled}
        // eslint-disable-next-line @typescript-eslint/no-shadow
        isDayHighlighted={(date: Moment) => date.isSame(today, 'day')}
        {...omit(props, ['data-testid'])}
      />
      {!!focusedInput && <Background onClick={handleOutsideClick} />}
    </DatePickerWrapper>
  );
}

const StyledDatePicker = styled(DatePicker)`
  /* Workaround to hide malformed calendar popup close button */
  & .SingleDatePicker + button {
    display: none;
  }

  & .SingleDatePicker,
  & .SingleDatePickerInput,
  & .SingleDatePickerInput_calendarIcon {
    height: 40px;
  }

  & .SingleDatePicker {
    width: 160px;
  }

  & .SingleDatePicker_picker {
    border-radius: 4px;
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2);
    border: solid 1px rgba(0, 0, 0, 0.14);
    background-color: ${color('-white')};
  }

  & .SingleDatePickerInput {
    background-color: ${color('--white')};
    color: ${color('--gray-800')};
    border: 1px solid ${color('--gray-400')};
    border-radius: 4px;
    display: flex;
    align-items: center;
    padding-right: 32px;

    &:focus-within {
      border: 1px solid ${color('--blue-400')};
    }
  }

  .SingleDatePickerInput__disabled {
    background: ${color('--gray-050')} !important;
  }

  & .SingleDatePickerInput_calendarIcon {
    height: 100%;
    margin: 0;
    min-width: 40px;
    padding: 0;
  }

  & .DateInput {
    background-color: transparent;
    color: ${color('--black-opacity-84')};
    height: 100%;
    width: 100%;

    & .DateInput_input {
      border: 0;
      background-color: transparent;
      ${theme('--font-normal')}
      font-family: ${theme('--font')};
      font-weight: normal;
      height: 100%;
      padding: 0;
    }
  }

  & .SingleDatePickerInput_clearDate {
    margin: 0;
    width: 36px;

    &:hover {
      background: transparent;
    }

    svg {
      height: 12px;
      width: 12px;
    }
  }

  &.clear-visible .SingleDatePickerInput_clearDate {
    display: inline-block !important;
  }

  & .CalendarMonth_caption {
    ${theme('--font-normal')}
    color: ${color('--black-opacity-84')};
    font-family: ${theme('--font')};
    padding: 8px 0 52px;
  }

  & .DayPicker_weekHeader {
    color: ${color('--black-opacity-84')};
    font-family: ${theme('--font')};
    font-weight: bold;
  }

  & .CalendarDay__default {
    border: 0;
    padding: 0;
    font-family: ${theme('--font')};
    color: ${color('--black-opacity-84')};

    &:hover {
      background: ${color('--black-200')};
      border-radius: 50%;
    }

    &:focus {
      outline: 0;

      ${Day} {
        align-items: center;
        border-radius: 50%;
        border: 2px solid #0081a1;
        display: flex;
        height: 100%;
        justify-content: center;
      }
    }
  }

  & .CalendarDay__blocked_out_of_range {
    color: ${color('--black-opacity-36')};
  }

  & .CalendarDay__selected_span {
    color: ${color('--black-opacity-84')};
    background: ${color('--blue-150')};
  }

  & .CalendarDay__selected {
    border-radius: 50%;
    color: ${color('--white')};
    background: #0081a1;
    font-weight: bold;
  }

  & .CalendarDay__selected:hover {
    background: ${color('--blue-500')};
  }

  & .DayPickerNavigation {
    display: flex;
    justify-content: space-between;
    padding: 12px 12px 0;
  }
`;

StyledDatePicker.displayName = 'DatePicker';

export default StyledDatePicker;
